import {
  ActionMenuItem,
  ActionButton,
  BodyText,
  ControlledNumberField,
  ControlledSelect,
  ControlledTextField,
  FieldGrid,
  FieldsetCard,
  Help,
  Icon,
  SectionTitle,
  useCan,
  useFormContainerContext,
} from '@if/mui'
import { DateOnly, isAfter, isWithinInterval } from '@if/core/date'
import {
  EApplicantType,
  EBeneficiaryType,
  EFinancingProgram,
  ELoanPurpose,
  EMerchantType,
  EPaymentPlan,
  ERelationToApplicant,
} from '@if/core/types'
import { formatUtcIsoDateTime } from '@if/core/utils'

import {
  Box,
  Card,
  CardContent,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  styled,
} from '@mui/material'
import { useBeneficiaryTypes } from '@src/containers/ViewCreditApplicationPage/credit-hooks'
import { transformAddressFromApi, transformApplicantJobFromApi } from '@src/data/api/credit-api/credit-transform'
import { useProfileLanguage } from '@src/hooks/language-hooks'
import { useMerchant } from '@src/containers/App/MerchantProvider'
import { useMerchantById, useMerchantByParentId } from '@src/data/api/merchants-api/merchants-api'
import { programConfigByFinancingProgramId } from '@src/data/creditapp-selectors'
import { toELanguage } from '@src/data/language-selectors'
import { access } from '@src/access'
import { Address } from '@src/types/AddressSchema'
import { SymTechInsuranceProviderId, useDevTools } from '@src/types/Constants'
import { ApplicantDto, CreditApplication, DraftApplicantSchema } from '@src/types/CreditApplicationSchema'
import { Merchant } from '@src/types/Merchant'
import { Star } from 'lucide-react'
import { useEffect, useMemo } from 'react'
import SymTechPrimeQuestion from './SymTechPrimeQuestion'
import { useLocation } from 'react-router-dom'
import { FieldErrors, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import ApplicantSections from './Applicant/ApplicantSections'
import BeneficiaryForm from './Applicant/components/BeneficiaryForm'
import MerchantSelector from './Applicant/components/MerchantSelector'
import { useTestCaseDialog } from './ListTestCaseDialog'

// A titleless card that mirrors FieldsetCard's focus-within border accent so the
// first (untitled) section highlights identically to the titled FieldsetCards.
const FocusCard = styled(Card)(({ theme }) => ({
  transition: theme.transitions.create('border-color'),
  // Match FieldsetCard's focus cue — the splash "baby blue" accent: `secondary` in
  // light, `primary` in dark (both land on #08ABF0). Was `primary` in both, which
  // rendered navy (near-black) in light.
  '&:focus-within': {
    borderColor: theme.vars.palette.secondary.main,
    ...theme.applyStyles('dark', { borderColor: theme.vars.palette.primary.main }),
  },
}))

interface Props {
  /** `create` for a new application; `edit` when reopening an existing one. */
  mode: 'create' | 'edit'
  financingProgramId: EFinancingProgram
  editDisabled: boolean
  isPrequalComplete: boolean
  isApplicantSinNeeded: boolean | null
  isCoapplicantSinNeeded: boolean | null
  lockApplicantIncomeAndExpenses?: boolean
  lockCoApplicantIncomeAndExpenses?: boolean
  hasPrequalifyBeenPressed: boolean
  coappChecked: boolean
  setCoappChecked: (checked: boolean) => void
  addCoApp: boolean
  hasExistingCoApplicant: boolean
  onSelectedMerchantChange: (merchant: Merchant | null) => void
}

const ApplicationFormBody = ({
  mode,
  financingProgramId,
  editDisabled,
  isPrequalComplete,
  isApplicantSinNeeded,
  isCoapplicantSinNeeded,
  lockApplicantIncomeAndExpenses,
  lockCoApplicantIncomeAndExpenses,
  hasPrequalifyBeenPressed,
  coappChecked,
  setCoappChecked,
  addCoApp,
  hasExistingCoApplicant,
  onSelectedMerchantChange,
}: Props) => {
  const { t } = useTranslation()
  const {
    control,
    watch,
    setValue,
    getValues,
    reset,
    formState: { errors },
  } = useFormContext<CreditApplication>()

  const merchant = useMerchant()
  const profileLanguage = useProfileLanguage()
  const programConfig = programConfigByFinancingProgramId(financingProgramId)
  const isBanner = merchant.merchantTypeId === EMerchantType.Banner
  const [merchantsList] = useMerchantByParentId(merchant.id)
  const watchedMerchantId = watch('merchantId')
  // The banner-selected merchant depends on the watched `merchantId` input (so it can't be a PageContainer
  // resource) — gate the form through `setBusy` while it loads so submit can't fire on stale merchant data.
  const [bannerSelectedMerchant, isFetchingBannerMerchant] = useMerchantById(isBanner ? watchedMerchantId : '')
  const selectedMerchant = isBanner ? bannerSelectedMerchant : merchant
  const { setBusy } = useFormContainerContext()
  useEffect(() => {
    setBusy('bannerMerchant', isFetchingBannerMerchant)
  }, [isFetchingBannerMerchant, setBusy])

  const isSymTechPrimeEnabled = useCan(access.symTechPrime)
  const showSymTechPrimeQuestion =
    mode === 'create' &&
    financingProgramId === EFinancingProgram.Products &&
    isSymTechPrimeEnabled &&
    (selectedMerchant?.insuranceProviderIds?.includes(SymTechInsuranceProviderId) ?? false)

  // Reset the SymTech prime flag when the question no longer applies (ex: merchant changed)
  useEffect(() => {
    if (!showSymTechPrimeQuestion && watch('isSymTechPrime')) {
      setValue('isSymTechPrime', false)
    }
  }, [showSymTechPrimeQuestion, setValue, watch])

  const isCustomBuydownActive = useCan(access.customBuydownActive, { merchant: selectedMerchant })

  const testCaseDialog = useTestCaseDialog()

  useEffect(() => {
    onSelectedMerchantChange(selectedMerchant)
  }, [selectedMerchant, onSelectedMerchantChange])

  const loanPurposeId = watch('loanPurposeId') as ELoanPurpose
  const beneficiaryTypeId = Number(watch('beneficiaryTypeId')) as EBeneficiaryType
  const isVeterinaryLoanPurpose = ELoanPurpose.Veterinary === loanPurposeId
  const isGoodAndServiceLoanPurpose = ELoanPurpose.GoodsAndServices === loanPurposeId
  const isHomeSolutionsLoanPurpose = ELoanPurpose.HomeSolutions === loanPurposeId
  const disableOtherName = beneficiaryTypeId !== EBeneficiaryType.Other
  const beneficiaryTypes = useBeneficiaryTypes(loanPurposeId)

  const activePaymentPlans = useMemo(() => {
    if (!selectedMerchant?.paymentPlans) return []
    const currentDate = new Date()
    const DISTANT_FUTURE = new Date()
    DISTANT_FUTURE.setFullYear(DISTANT_FUTURE.getFullYear() + 100)
    const currentUtcDateFormatted = new Date(formatUtcIsoDateTime(new Date()))

    return selectedMerchant.paymentPlans.filter((plan) => {
      const planActiveFrom = new Date(plan.activeFrom)
      const planExpiresOn = new Date(plan?.expiresOn ?? DISTANT_FUTURE)
      return (
        isWithinInterval(currentDate, { start: planActiveFrom, end: planExpiresOn }) &&
        isAfter(planExpiresOn, currentUtcDateFormatted)
      )
    })
  }, [selectedMerchant])
  const hasActivePaymentPlans = activePaymentPlans.length > 0

  const planPromoRuleDefaultPlanId = useMemo(() => {
    if (!selectedMerchant?.planPromoRules?.length || !activePaymentPlans.length) return null
    const starRules = [...selectedMerchant.planPromoRules]
      .filter((r) => /^\*+$/.test(r.planName))
      .sort((a, b) => b.planName.length - a.planName.length)
    if (!starRules.length) return null
    const matchingPlan = activePaymentPlans.find((p) => p.promoCode === starRules[0].defaultPromoCode)
    return matchingPlan?.id ?? null
  }, [selectedMerchant?.planPromoRules, activePaymentPlans])

  const promoCodeMinStars = useMemo(() => {
    const map = new Map<string, number>()
    if (!selectedMerchant?.planPromoRules) return map
    for (const rule of selectedMerchant.planPromoRules) {
      if (!/^\*+$/.test(rule.planName)) continue
      const stars = rule.planName.length
      const codes = [rule.defaultPromoCode, ...(rule.selectablePromoCodes ?? [])]
      for (const code of codes) {
        const current = map.get(code)
        if (current === undefined || stars < current) map.set(code, stars)
      }
    }
    return map
  }, [selectedMerchant?.planPromoRules])

  const paymentPlanOptions = useMemo(
    () => [
      <ActionMenuItem key="default" value={EPaymentPlan.RegularDailyInterests}>
        {t('common.regularPlan')}
      </ActionMenuItem>,
      ...activePaymentPlans.map((plan) => {
        const promoCode = plan.promoCode.replace('PLAN', '').trim()
        const minStars = promoCodeMinStars.get(plan.promoCode) ?? promoCodeMinStars.get(promoCode)
        return (
          <ActionMenuItem key={plan.id} value={plan.id}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {promoCode}
              {minStars != null && (
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', ml: 0.5 }}>
                  ({minStars}
                  <Icon icon={Star} size="xs" />
                  {minStars < 5 && '+'})
                </Box>
              )}
            </Box>
          </ActionMenuItem>
        )
      }),
    ],
    [activePaymentPlans, t, promoCodeMinStars],
  )

  // Keep beneficiary / loan-purpose / invisalign / merchant fields consistent.
  useEffect(() => {
    if (!isBanner) {
      setValue('merchantId', merchant.id, { shouldValidate: true })
      setValue('loanPurposeId', (merchant.serviceCategory ?? '') as ELoanPurpose, { shouldValidate: true })
    }
    if (isVeterinaryLoanPurpose) {
      setValue('beneficiaryTypeId', EBeneficiaryType.Other, { shouldValidate: true })
      setValue('otherBeneficiaryFirstName', '', { shouldValidate: true })
    }
    if (beneficiaryTypeId === EBeneficiaryType.Applicant || beneficiaryTypeId === EBeneficiaryType.Coapplicant) {
      setValue('otherBeneficiaryFirstName', '', { shouldValidate: true })
      setValue('otherBeneficiaryLastName', '', { shouldValidate: true })
    }
    if (isGoodAndServiceLoanPurpose || isHomeSolutionsLoanPurpose) {
      setValue('beneficiaryTypeId', EBeneficiaryType.Applicant, { shouldValidate: true })
    }
    if (loanPurposeId !== ELoanPurpose.Dentistry && watch('isInvisalign')) {
      setValue('isInvisalign', false)
    }
    if (financingProgramId === EFinancingProgram.Products) {
      setValue('requestedLoanAmount', 0)
    }
  }, [
    merchant,
    isBanner,
    setValue,
    isVeterinaryLoanPurpose,
    beneficiaryTypeId,
    isGoodAndServiceLoanPurpose,
    financingProgramId,
    isHomeSolutionsLoanPurpose,
    loanPurposeId,
    watch,
  ])

  useEffect(() => {
    if (planPromoRuleDefaultPlanId && !getValues('merchantPaymentPlanId')) {
      setValue('merchantPaymentPlanId', planPromoRuleDefaultPlanId, { shouldValidate: true })
      const plan = activePaymentPlans.find((p) => p.id === planPromoRuleDefaultPlanId)
      if (plan) setValue('prequalPromoCode', plan.promoCode)
    }
  }, [planPromoRuleDefaultPlanId, getValues, setValue, activePaymentPlans])

  const handleToggleCoapplicant = (checked: boolean) => {
    if (checked) {
      setValue('coApplicant', {
        ...(DraftApplicantSchema().getDefault() as unknown as ApplicantDto),
        languageId: toELanguage(profileLanguage),
      })
      setValue('coApplicant.isPrimaryApplicant', false)
    } else {
      setValue('coApplicant', null)
    }
    setCoappChecked(checked)
  }

  useEffect(() => {
    if (addCoApp) handleToggleCoapplicant(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addCoApp])

  // Deep-link: scroll to the #applicant / #coapplicant anchor when navigated to with a hash.
  const { hash } = useLocation()
  useEffect(() => {
    if (!hash) return
    document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [hash])

  const onMerchantChange = (_event: unknown, newMerchant: Merchant | null) => {
    if (!newMerchant) return
    setValue('merchantId', newMerchant.id, { shouldValidate: true })
    setValue('loanPurposeId', (newMerchant.serviceCategory ?? '') as ELoanPurpose, { shouldValidate: true })
    setValue('merchantPaymentPlanId', EPaymentPlan.RegularDailyInterests, { shouldValidate: true })
    setValue('prequalPromoCode', null)
    if (newMerchant.serviceCategory === ELoanPurpose.GoodsAndServices) {
      setValue('beneficiaryTypeId', EBeneficiaryType.Applicant, { shouldValidate: true })
      setValue('otherBeneficiaryFirstName', '', { shouldValidate: true })
      setValue('otherBeneficiaryLastName', '', { shouldValidate: true })
    }
  }

  const onTestSelected = (applicant: ApplicantDto, type: EApplicantType) => {
    const transformedAddress = transformAddressFromApi(applicant.currentAddress as Address)
    applicant.currentAddress = { ...transformedAddress, years: transformedAddress.years ?? 0 }
    applicant.currentJobs = applicant.currentJobs.map(transformApplicantJobFromApi)
    applicant.previousJobs = applicant.previousJobs.map(transformApplicantJobFromApi)
    // Test-case data arrives from the API with `birthDate` as a wire string; the
    // dateOnly picker needs a DateOnly, so coerce before resetting it into the form.
    if (applicant.birthDate) applicant.birthDate = new DateOnly(applicant.birthDate)
    if (type === EApplicantType.CoApplicant) {
      applicant.isPrimaryApplicant = false
      applicant.relationWithApplicant = ERelationToApplicant.CommonLaw
      reset({ ...getValues(), coApplicant: applicant })
    } else {
      applicant.isPrimaryApplicant = true
      reset({ ...getValues(), applicant })
    }
  }

  // Dev-only "load a test case" button; call sites gate it behind `useDevTools`.
  const testAction = (type: EApplicantType) => (
    <ActionButton
      emphasis="normal"
      id="testCase"
      onClick={() => {
        void testCaseDialog.create().then((applicant) => {
          if (applicant) onTestSelected(applicant, type)
        })
      }}
    >
      {t('common.testCase')}
    </ActionButton>
  )

  return (
    <>
      <Stack spacing={3}>
        <FocusCard>
          <CardContent>
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 2, md: 1 }}>
                <MerchantSelector
                  isBanner={isBanner}
                  merchantsList={merchantsList}
                  onChange={onMerchantChange}
                  errors={errors}
                  merchant={selectedMerchant}
                  financingProgramId={financingProgramId}
                />
                <ControlledTextField
                  id="merchantReferenceCodeId"
                  name="merchantReferenceCode"
                  label={
                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                      {t('applicationHeader.yourReferenceCode')}
                      <Help title={t('applicationHeader.tooltips.merchantReferenceCodeInfo')} />
                    </Box>
                  }
                  control={control}
                  fullWidth
                />
              </Stack>

              {programConfig.supportsSinglePayment && (
                <FormControl component="fieldset">
                  <FormLabel component="legend">{t('editCreditApplication.paymentOptions')}</FormLabel>
                  <RadioGroup
                    row
                    value={watch('isSinglePayment') === true ? 'single' : 'loan'}
                    onChange={(event) => setValue('isSinglePayment', event.target.value === 'single')}
                    sx={{ pl: 1, gap: 4 }}
                  >
                    <FormControlLabel
                      value="single"
                      control={<Radio />}
                      label={t('editCreditApplication.singlePayment')}
                    />
                    <FormControlLabel value="loan" control={<Radio />} label={t('editCreditApplication.financing')} />
                  </RadioGroup>
                </FormControl>
              )}

              <FieldGrid columns={{ xs: 1, md: 3 }}>
                {programConfig.loanPurposeIdIsRequired && (
                  <ControlledTextField
                    name="loanPurposeId"
                    control={control}
                    id="loanPurposeId"
                    label={t('editCreditApplication.loanPurpose')}
                    fullWidth
                    disabled
                  />
                )}
                {programConfig.requestedLoanAmountIsRequired && (
                  <ControlledNumberField
                    id="amountRequested"
                    name="requestedLoanAmount"
                    control={control}
                    label={t('editCreditApplication.amountRequested')}
                    fullWidth
                    isMoney
                  />
                )}
                {hasActivePaymentPlans && !isCustomBuydownActive && (
                  <ControlledSelect
                    control={control}
                    name="merchantPaymentPlanId"
                    options={paymentPlanOptions}
                    label={t('worksheet.paymentPlan')}
                    fullWidth
                    disabled={editDisabled || isPrequalComplete}
                    disableEmptyOption
                    onChange={(planId) => {
                      const plan = activePaymentPlans.find((p) => p.id === planId)
                      setValue('prequalPromoCode', plan?.promoCode ?? null)
                    }}
                  />
                )}
              </FieldGrid>

              {loanPurposeId === ELoanPurpose.Dentistry && (
                <FormControl component="fieldset" disabled={editDisabled}>
                  <FormLabel component="legend">{t('editCreditApplication.invisalign.question')}</FormLabel>
                  {/* TODO: convert to a ControlledRadioGroup */}
                  <RadioGroup
                    row
                    value={watch('isInvisalign') === true ? 'yes' : 'no'}
                    onChange={(event) => setValue('isInvisalign', event.target.value === 'yes')}
                    sx={{ pl: 1, gap: 4 }}
                  >
                    <FormControlLabel value="no" control={<Radio />} label={t('common.no')} disabled={editDisabled} />
                    <FormControlLabel value="yes" control={<Radio />} label={t('common.yes')} disabled={editDisabled} />
                  </RadioGroup>
                </FormControl>
              )}

              {showSymTechPrimeQuestion && (
                <SymTechPrimeQuestion onChange={(value) => setValue('isSymTechPrime', value)} disabled={editDisabled} />
              )}

              {programConfig.beneficiaryIsRequired && !(isGoodAndServiceLoanPurpose || isHomeSolutionsLoanPurpose) && (
                <BeneficiaryForm
                  beneficiaryTypes={beneficiaryTypes}
                  isVeterinaryLoanPurpose={isVeterinaryLoanPurpose}
                  disableOtherName={disableOtherName}
                  formControl={control}
                />
              )}
            </Stack>
          </CardContent>
        </FocusCard>

        {useDevTools && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>{testAction(EApplicantType.Applicant)}</Box>
        )}

        <div id="applicant">
          <SectionTitle>{t('common.applicant')}</SectionTitle>
          <Divider sx={{ mt: 1 }} />
        </div>

        <ApplicantSections
          control={control}
          watch={watch}
          setValue={setValue}
          errors={errors.applicant as FieldErrors<ApplicantDto> | undefined}
          applicantType={EApplicantType.Applicant}
          editDisabled={editDisabled || (lockApplicantIncomeAndExpenses ?? false) || isPrequalComplete}
          financingProgramId={financingProgramId}
          hasPrequalifyBeenPressed={hasPrequalifyBeenPressed}
          isSinNeeded={isApplicantSinNeeded}
        />

        {programConfig.supportsCoApplicant && (
          <div id="coapplicant">
            <FieldsetCard
              title={t('newApplication.addCoApplicant')}
              action={
                <Switch
                  id="toggleCoapplicant"
                  checked={coappChecked}
                  onChange={(e) => handleToggleCoapplicant(e.target.checked)}
                />
              }
            >
              <BodyText>{t('newApplication.addCoApplicantSubtitle')}</BodyText>
            </FieldsetCard>
          </div>
        )}

        {programConfig.supportsCoApplicant && coappChecked && (
          <>
            {useDevTools && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>{testAction(EApplicantType.CoApplicant)}</Box>
            )}
            <div>
              <SectionTitle>{t('common.coApplicant')}</SectionTitle>
              <Divider sx={{ mt: 1 }} />
            </div>
            <ApplicantSections
              control={control}
              watch={watch}
              setValue={setValue}
              errors={errors.coApplicant as FieldErrors<ApplicantDto> | undefined}
              applicantType={EApplicantType.CoApplicant}
              editDisabled={
                editDisabled ||
                (lockCoApplicantIncomeAndExpenses ?? false) ||
                (isPrequalComplete && hasExistingCoApplicant && !addCoApp)
              }
              financingProgramId={financingProgramId}
              hasPrequalifyBeenPressed={hasPrequalifyBeenPressed}
              isSinNeeded={isCoapplicantSinNeeded}
            />
          </>
        )}
      </Stack>
    </>
  )
}

export default ApplicationFormBody
